import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Fetcher } from '@this/src/util';

type ServerZone = {
  code: string;
  name: string;
};

type ServerIpv4 = {
  address: string;
};

type ServerResponseArgs = {
  id: number;
  name: string;
  cpu_cores: number;
  memory_mebibytes: number;
  zone: ServerZone;
  ipv4: ServerIpv4;
  power_status: string;
  service_status: string;
  last_fetched_at?: string | null;
};

type ServersResponse = {
  count: number;
  results: ServerResponseArgs[];
};

const ServerManagementTable: React.FC = () => {
  const [servers, setServers] = useState<ServerResponseArgs[]>([]);
  const [loading, setLoading] = useState(true);
  const [rebootingIds, setRebootingIds] = useState<Set<number>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchServers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Fetcher.get<ServersResponse>('/god/sakura_servers');
      setServers(res.results ?? []);
    } catch {
      setErrorMessage('サーバ一覧の取得に失敗しました。');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  const handleForceReboot = useCallback(async (serverId: number, serverName: string) => {
    if (!window.confirm(`${serverName} を強制再起動しますか？`)) return;

    setRebootingIds(prev => new Set(prev).add(serverId));
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      await Fetcher.post(`/god/sakura_servers/${serverId}/force_reboot`, {});
      setSuccessMessage(`${serverName} の再起動リクエストを送信しました。`);
    } catch {
      setErrorMessage(`${serverName} の再起動に失敗しました。`);
    }
    setRebootingIds(prev => {
      const next = new Set(prev);
      next.delete(serverId);
      return next;
    });
  }, []);

  if (loading) return <Loading />;

  return (
    <div style={{ margin: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>さくらVPS サーバ管理</h2>
        <Button onClick={fetchServers} size="small">
          更新
        </Button>
      </div>

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} style={{ marginBottom: '16px' }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
          {errorMessage}
        </Alert>
      )}

      {servers.length === 0 ? (
        <p style={{ color: '#666' }}>サーバが見つかりませんでした。</p>
      ) : (
        <table style={{ fontSize: '12px', borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '80px' }} />
            <col />
            <col style={{ width: '110px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '50px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '100px' }} />
          </colgroup>
          <thead>
            <tr>
              <Th>サーバID</Th>
              <Th>サーバ名</Th>
              <Th>IPアドレス</Th>
              <Th>最終データ取得時刻</Th>
              <Th>CPU</Th>
              <Th>メモリ(MiB)</Th>
              <Th>電源</Th>
              <Th>操作</Th>
            </tr>
          </thead>
          <tbody>
            {servers.map(server => (
              <tr key={server.id}>
                <Td>{server.id}</Td>
                <Td
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={server.name}
                >
                  {server.name}
                </Td>
                <Td>{server.ipv4?.address}</Td>
                <Td>{server.last_fetched_at ? moment(server.last_fetched_at).format('YYYY-MM-DD HH:mm') : '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{server.cpu_cores}</Td>
                <Td style={{ textAlign: 'right' }}>{server.memory_mebibytes}</Td>
                <Td>
                  <StatusBadge status={server.power_status} />
                </Td>
                <Td>
                  <Button
                    onClick={() => handleForceReboot(server.id, server.name)}
                    disabled={rebootingIds.has(server.id)}
                    loading={rebootingIds.has(server.id)}
                    size="small"
                  >
                    強制再起動
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th
    style={{
      textAlign: 'left',
      borderBottom: '1px solid #ccc',
      padding: '6px 8px',
      whiteSpace: 'nowrap',
      backgroundColor: '#f5f5f5'
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties; title?: string }> = ({
  children,
  style,
  title
}) => (
  <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px', whiteSpace: 'nowrap', ...style }} title={title}>
    {children}
  </td>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const color = status === 'power_on' ? '#28a745' : status === 'power_off' ? '#dc3545' : '#6c757d';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: color,
        color: '#fff',
        fontSize: '11px'
      }}
    >
      {status}
    </span>
  );
};

export default ServerManagementTable;
